import { Injectable } from '@angular/core';
import 'froala-editor/js/plugins/align.min.js';
import 'froala-editor/js/plugins/char_counter.min.js';
import 'froala-editor/js/plugins/table.min.js';
import 'froala-editor/js/plugins/code_view.min.js';
import 'froala-editor/js/plugins/font_size.min.js';
import 'froala-editor/js/plugins/link.min.js';
import 'froala-editor/js/plugins/lists.min.js';
import 'froala-editor/js/plugins/paragraph_style.min.js';
import 'froala-editor/js/plugins/paragraph_format.min.js';
import 'froala-editor/js/plugins/image.min.js';
import 'froala-editor/js/plugins/video.min.js';
import 'froala-editor/js/plugins/colors.min.js';
import 'froala-editor/js/plugins/font_family.min.js';

import FroalaEditor from 'froala-editor';
import { environment } from '@env/environment';

export interface FroalaEditorOptions {
  events?: {
    initialized?: (e: any) => void;
    contentChanged?: () => void;
  };
  [key: string]: any;
}

@Injectable({
  providedIn: 'root'
})
export class FroalaEditorService {

  constructor() { }


  private getBaseEditorOptions(): FroalaEditorOptions {
    return {
      attribution: false,
      autofocus: true,
      charCounterCount: true,
      htmlExecuteScripts: false,
      htmlRemoveTags: ['script', 'base'],
      pluginsEnabled: [
        'align', 'charCounter', 'codeView', 'fontSize', 'lists', 'image', 'colors',
        'paragraphStyle', 'table', 'url', 'paragraphFormat', 'addPhishLinkPlugin', 'link', 'fontFamily'
      ],
      height: '100%',
      imageInsertButtons: ['imageUpload', '|', 'imageByURL'],
      useClasses: false,
      imageUpload: true,
      imagePaste: false,
      editorClass: '',
      key: environment.froalaApiKey,
      linkEditButtons: ['linkRemove'],
      requestWithCredentials: true,
      imageUploadParam: 'image',
      imageUploadMethod: 'POST',
      imageMaxSize: 5 * 1024 * 1024,
      imageAllowedTypes: ['jpeg', 'jpg', 'png'],
      fontFamily: {
        "'Open Sans',sans-serif": 'Open Sans',
        "'Open Sans ExtraBold',sans-serif": 'Open Sans ExtraBold',
        "'Open Sans Condensed',sans-serif": 'Open Sans Condensed',
        "Roboto,sans-serif": 'Roboto',
        "Oswald,sans-serif": 'Oswald',
        "Montserrat,sans-serif": 'Montserrat',
        "Arial,Helvetica,sans-serif": 'Arial',
        "Georgia,serif": 'Georgia',
        'Impact,Charcoal,sans-serif': 'Impact',
        "Tahoma,Geneva,sans-serif": 'Tahoma',
        "'Times New Roman',Times,serif": 'Times New Roman',
        "Verdana,Geneva,sans-serif": 'Verdana'
      },
      fontFamilySelection: true,
    };
  }

  getCourseContentEditorOptions(cpId: string, callbacks?: {
    onInitialized?: (editor: any) => void;
    onContentChanged?: () => void;
  }): FroalaEditorOptions {
    return {
      ...this.getBaseEditorOptions(),
      events: {
        'initialized': (e: any) => {
          callbacks?.onInitialized?.(e.getEditor());
        },
        'contentChanged': () => {
          callbacks?.onContentChanged?.();
        }
      },
      fullPage: false,
      pluginsEnabled: [
        'align', 'charCounter', 'codeView', 'fontSize', 'lists', 'image', 'colors',
        'paragraphStyle', 'table', 'url', 'paragraphFormat', 'addPhishLinkPlugin', 'link', 'fontFamily', 'video'
      ],
      toolbarButtons: [
        ['bold', 'italic', 'underline', 'textColor', 'clearFormatting', 'fontSize', 'fontFamily', 'dynamicContent'],
        ['alignDropdown', 'formatOLSimple', 'formatUL', 'outdent', 'indent', 'leftToRight', 'rightToLeft'],
        ['insertLink', 'addPhishLink', 'insertImage', 'insertVideo', 'insertTable', 'insertFile', 'html', 'undo', 'redo']
      ],
      imageEditButtons: ['imageReplace', 'imageDisplay', 'imageAlign', 'imageInfo', 'imageRemove', 'imagePhishLink'],
      videoUpload: false,
      imageUploadURL: `${environment.api}/courses/customer_courses/image_upload/`,
      imageUploadParams: {
        cp: cpId,
      },
      iframe: true,
      iframeStyleFiles: [
        environment.froalaCoursesExternalCss,
      ],
    };
  }

  getCampaignEditorOptions(cpId: string, callbacks?: {
    onInitialized?: (editor: any) => void;
    onContentChanged?: () => void;
  }): FroalaEditorOptions {
    return {
      ...this.getBaseEditorOptions(),
      events: {
        'initialized': (e: any) => {
          callbacks?.onInitialized?.(e.getEditor());
        },
        'contentChanged': () => {
          callbacks?.onContentChanged?.();
        }
      },
      fullPage: true,
      toolbarButtons: [
        ['bold', 'italic', 'underline', 'textColor', 'clearFormatting', 'fontSize', 'fontFamily', 'dynamicContent'],
        ['alignDropdown', 'formatOLSimple', 'formatUL', 'outdent', 'indent', 'leftToRight', 'rightToLeft'],
        ['addPhishLink', 'insertImage', 'insertTable', 'insertFile', 'html', 'undo', 'redo']
      ],
      imageEditButtons: ['imageDisplay', 'imageAlign', 'imageInfo', 'imageRemove', 'imagePhishLink'],
      zIndex: 9900,
      imageUploadURL: `${environment.api}/attack-image-upload/`,
      imageUploadParams: {
        cp: cpId,
      },
      fontFamilyDefaultSelection: 'Arial'
    };
  }

  getSmishingEditorOptions(cpId: string, callbacks?: {
    onInitialized?: (editor: any) => void;
    onContentChanged?: () => void;
  }): FroalaEditorOptions {
    return {
      ...this.getBaseEditorOptions(),
      events: {
        'initialized': (e: any) => {
          callbacks?.onInitialized?.(e.getEditor());
        },
        'contentChanged': () => {
          callbacks?.onContentChanged?.();
        }
      },
      fullPage: true,
      height: 'calc(80vh - 235px)',
      heightMax: 'calc(80vh - 290px)',
      toolbarButtons: [
        ['bold', 'italic', 'underline', 'textColor', 'clearFormatting', 'fontSize', 'fontFamily'],
        ['alignDropdown', 'formatOLSimple', 'formatUL', 'outdent', 'indent'],
        ['addPhishLink', 'insertImage', 'insertTable', 'insertFile', 'html', 'undo', 'redo', 'custom_tags_dropdown']
      ],
      imageEditButtons: ['imageDisplay', 'imageAlign', 'imageInfo', 'imageRemove', 'imagePhishLink'],
      zIndex: 9900,
      imageUploadURL: `${environment.api}/attack-image-upload/`,
      imageUploadParams: {
        cp: cpId,
      },
      fontFamilyDefaultSelection: 'Arial'
    };
  }

  addDirectionsButtonPlugin() {
    FroalaEditor.DefineIcon('rightToLeftIcon', { NAME: 'arrow-right', SRC: '/assets/svg/rtl.svg', alt: '', template: 'image' });
    FroalaEditor.RegisterCommand('rightToLeft', {
      title: 'Text direction: Right to Left',
      icon: 'rightToLeftIcon',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        FroalaEditorService.changeDirection(this, 'rtl', 'right');
      }
    });

    FroalaEditor.DefineIcon('leftToRight', { NAME: 'arrow-left', SRC: '/assets/svg/ltr.svg', alt: '', template: 'image' });
    FroalaEditor.RegisterCommand('leftToRight', {
      title: 'Text direction: Left to Right',
      focus: true,
      undo: true,
      refreshAfterCallback: true,
      callback: function () {
        FroalaEditorService.changeDirection(this, 'ltr', 'left');
      }
    });
  }

  addDynamicContentPlugin() {
    FroalaEditor.DefineIcon('dynamicContent', { NAME: 'Add Dynamic Content', template: 'text' });
    FroalaEditor.RegisterCommand('dynamicContent', {
      title: 'Insert Dynamic Content',
      type: 'dropdown',
      options: {
        '{{sender_name}}': '{{sender_name}}',
        '{{first_name}}': '{{first_name}}',
        '{{customer_name}}': '{{customer_name}}',
        '{{year}}': '{{year}}',
        '{{date}}': '{{date}}',
        'learn_more': 'Learn More',
      },
      callback: function (cmd, val) {
        if (val == 'learn_more') {
          const url = 'https://support.cybeready.com/hc/en-us/articles/16264794058652-Dynamic-Content-in-Phishing-Simulations';
          window.open(url, '_blank');
        } else {
          this.html.insert(val);
          this.events.focus();
        }
      },
    });
  }

  /**
   * Removes Froala editor's default table border styles that get injected on save.
   * Froala adds `border: Npx solid rgb(221, 221, 221)` to table cells.
   * This only targets table-related elements (table, tr, td, th, tbody, thead, tfoot).
   */
  static sanitizeFroalaTableBorders(html: string): string {
    if (!html) return html;

    const div = document.createElement('div');
    div.innerHTML = html;

    div.querySelectorAll('table, tr, td, th, tbody, thead, tfoot').forEach(el => {
      const style = el.getAttribute('style');
      if (style) {
        const cleaned = style.replace(/\s*border:\s*\d+px\s+solid\s+rgb\(221,\s*221,\s*221\);?/gi, '');
        if (cleaned.trim()) {
          el.setAttribute('style', cleaned);
        } else {
          el.removeAttribute('style');
        }
      }
    });

    return div.innerHTML;
  }

  static changeDirection(editorInstance: any, dir: string, align: string): void {
    // Save selection and wrap blocks.
    editorInstance.selection.save();
    editorInstance.html.wrap(true, true, true, true);
    editorInstance.selection.restore();

    // Get blocks.
    const elements = editorInstance.selection.blocks();

    // Save selection again.
    editorInstance.selection.save();

    for (const element of elements) {
      if (element !== editorInstance.el) {
        editorInstance.$(element)
          .css('direction', dir)
          .css('text-align', align)
          .removeClass('fr-temp-div');
      }
    }

    // Unwrap temporary divs and restore selection.
    editorInstance.html.unwrap();
    editorInstance.selection.restore();
  }
}
